import type { Sprite } from 'pixi.js';
import { Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import { RaceEventTrackLayout } from '../../../../layouts/raceEvent/RaceEventTrackLayout';
import type { RaceEventState } from '../types';
import { getRaceEventEndTime } from '../RaceEventUtils';
import type { LeaderboardEntry, PlatformUser } from '../../../app/platform/Platform';
import { attachIntervalToDisplayObject, CountDownFormat, formatIntoUnitCountdown } from '../../../utils/time-utils';
import { getLocalizedString } from '../../../app/localization/getLocalizedString';
import { RaceEventChestView } from './RaceEventChestView';
import { RaceEventEndSequenceHandler } from './track/RaceEventEndSequenceHandler';
import { getRaceLastSeenLeaderboard, getRaceLeaderboard } from '../raceOpponents';
import { RaceEventStartLightsLayout } from '../../../../layouts/raceEvent/RaceEventStartLightsLayout';
import { playLayoutAnimation } from '../../../libs/layouts/layoutAnimations/playLayoutAnimation';
import { RaceAvatarFrameView } from './track/RaceAvatarFrameView';
import { RaceEventCarView } from './track/RaceEventCarView';
import { RaceRankFlagView } from './track/RaceRankFlagView';
import { RaceEventGoTextLocalizations } from '../../../../layouts/raceEvent/RaceEventGoTextLocalizations';

export interface RaceTrackProgress {
  level: number;
  normProgress: number;
  rank: number;
}

interface RaceEventTrackViewConfig {
  state: RaceEventState;
  player: PlatformUser;
  endSequenceSlot: Container;
  coinTarget: Container;
  boosterTarget: Container;
  setupForRaceStartAnimation: boolean;
}

interface RacerEntry {
  avatar: RaceAvatarFrameView;
  car: RaceEventCarView;
  rank: RaceRankFlagView;
  trackHighlight: Sprite;
  initProgress: RaceTrackProgress;
}

export class RaceEventPopupTrackView extends Container {
  public onHideRequested = new Signal();

  public onHideOverlayRequested = new Signal();

  private readonly layout = new RaceEventTrackLayout();

  private readonly chestsViews: RaceEventChestView[] = [];

  private readonly racerViews: Record<string, RacerEntry> = {};

  constructor(private readonly config: RaceEventTrackViewConfig) {
    super();
    this.init();
  }

  private init(): void {
    const {
      config: { setupForRaceStartAnimation },
      layout: { root },
    } = this;

    root.props({
      parent: this,
    });

    this.initTimer();

    this.initChests();

    this.initFinishLine();

    this.initRacers();

    if (!setupForRaceStartAnimation) {
      this.startTrackScrolling();
    }
  }

  private leaderboardEntryToRaceTrackProgress(entry: LeaderboardEntry): RaceTrackProgress {
    const {
      config: {
        state: {
          config: { levelsToBeat },
        },
      },
    } = this;

    return {
      rank: entry.rank,
      level: entry.score,
      normProgress: entry.score / levelsToBeat,
    };
  }

  private initRacers(): void {
    const {
      layout: {
        carsContainer,
        carSlot,
        frameSlot,
        rankSlot,
        carStartPosition,
        carPreStartPosition,
        carFinishedPosition,
        carPreFinishedPosition,
        trackHighlight,
      },
      config: {
        setupForRaceStartAnimation,
        state,
        state: { opponents },
        player,
      },
    } = this;

    const leaderboard = getRaceLastSeenLeaderboard(state, player);

    const positions = {
      finishedPosition: carFinishedPosition,
      preFinishedPosition: carPreFinishedPosition,
      preStartPosition: carPreStartPosition,
      startPosition: carStartPosition,
    };

    if (opponents.length < 4) {
      console.warn(`Race Event: Expected 4 opponents, got ${opponents.length}`);
      return;
    }

    [opponents[0].id, opponents[1].id, player.id, opponents[2].id, opponents[3].id].forEach((id, index) => {
      const leaderboardEntry = leaderboard.find((entry) => entry.player.id === id);
      if (!leaderboardEntry) {
        throw new Error(`Race Event leaderboard doesn't contain expected entry`);
      }

      const initProgress = this.leaderboardEntryToRaceTrackProgress(leaderboardEntry);

      this.racerViews[leaderboardEntry.player.id] = {
        initProgress,
        trackHighlight: trackHighlight[index],
        car: new RaceEventCarView({
          initProgress,
          player: leaderboardEntry.player,
          positions,
          setupForRaceStartAnimation,
        }).props({
          parent: carsContainer,
          x: carSlot[index].x,
        }),
        avatar: new RaceAvatarFrameView(leaderboardEntry).props({
          parent: frameSlot[index],
        }),
        rank: new RaceRankFlagView(initProgress).props({
          parent: rankSlot[index],
        }),
      };
    });
  }

  private initTimer(): void {
    const { state } = this.config;
    const { root, timerText } = this.layout;

    attachIntervalToDisplayObject(
      root,
      () => {
        const timeLeft = getRaceEventEndTime(state) - Date.now();
        if (timeLeft < 0) {
          timerText.text = getLocalizedString('Ended');
          return;
        }
        timerText.text = formatIntoUnitCountdown(timeLeft, 2, CountDownFormat.DHM);
      },
      1000,
    );
  }

  private initFinishLine(): void {
    const {
      config: { state },
      layout: { goalText, finishLineText },
    } = this;

    const finishLineLevel = `${state.levelStart + state.config.levelsToBeat}`;
    finishLineText.text = finishLineText.text.replace('<nr>', finishLineLevel);
    goalText.text = goalText.text.replace('<nr>', finishLineLevel);
  }

  private initChests(): void {
    const {
      layout: { chestSlot, chestCloseInput },
      config: { state },
    } = this;

    chestCloseInput.on('pointerdown', () => {
      this.chestsViews.forEach((chest) => {
        chest.setRewardBubbleVisible(false);
      });
    });
    chestCloseInput.cursor = 'pointer';

    chestSlot.forEach((slot, index) => {
      const chestView = new RaceEventChestView(index + 1, state.config.rewards[index]).props({
        parent: slot,
        scale: 0.8,
      });

      chestView.onClicked.connect(() => {
        this.chestsViews.forEach((chest) => {
          chest.setRewardBubbleVisible(chest === chestView);
        });
      });

      this.chestsViews.push(chestView);
    });
  }

  private async animateRacerChange(racerViews: RacerEntry, progress: RaceTrackProgress): Promise<void> {
    const { car, avatar, rank, initProgress, trackHighlight } = racerViews;

    const progressChanged = initProgress.normProgress !== progress.normProgress;

    const promises: Promise<void>[] = [];

    if (progressChanged) {
      promises.push(car.animateChange(progress));
      trackHighlight.tweenTo({ alpha: 0.5 }, 200, 'cubicIn', true, 0, 0, true);
    }

    promises.push(rank.animateChange(progress));

    if (initProgress.level !== progress.level) {
      promises.push(avatar.changeLevel(progress.level));
    }

    racerViews.initProgress = progress;

    await Promise.all(promises);
  }

  public async animateUpdate(): Promise<void> {
    const {
      config: {
        state: {
          config: { levelsToBeat },
        },
        state,
        player,
      },
    } = this;

    const leaderboard = getRaceLeaderboard(state, player);

    await Promise.all(
      leaderboard.map(async (entry) => {
        const racerViews = this.racerViews[entry.player.id];

        if (!racerViews) return;

        await this.animateRacerChange(racerViews, {
          level: entry.score,
          normProgress: entry.score / levelsToBeat,
          rank: entry.rank,
        });
      }),
    );
  }

  public async startEndSequence(): Promise<void> {
    const {
      config: { state, player, endSequenceSlot, coinTarget, boosterTarget },
    } = this;

    const leaderboard = getRaceLeaderboard(state, player);

    const playerRank = leaderboard.find((entry) => entry.player.currentUser)?.rank ?? 0;

    const avatarFrames = leaderboard.map(({ player: { id } }) => this.racerViews[id].avatar);

    const endSequenceHandler = new RaceEventEndSequenceHandler({
      avatarFrames,
      chests: this.chestsViews,
      playerRank,
      coinTarget,
      boosterTarget,
    }).props({
      parent: endSequenceSlot,
    });

    endSequenceHandler.onHideDarkOverlay.connect(() => {
      this.onHideOverlayRequested.emit();
    });

    endSequenceHandler.onHideRequest.connect(() => {
      this.onHideRequested.emit();
    });

    await this.wait(50);
    await endSequenceHandler.play();
  }

  public async animateRaceStart(): Promise<void> {
    const {
      config: {
        state: { opponents },
        player,
      },
    } = this;

    if (opponents.length < 4) {
      console.warn(`Race Event: Expected 4 opponents for race start, got ${opponents.length}`);
      return;
    }

    const layout = new RaceEventStartLightsLayout();
    layout.root.props({
      parent: this.layout.lightsContainer,
    });

    const racers = [
      this.racerViews[opponents[0].id],
      this.racerViews[opponents[1].id],
      this.racerViews[player.id],
      this.racerViews[opponents[2].id],
      this.racerViews[opponents[3].id],
    ];

    racers.forEach(async (racerEntry, index) => {
      await this.wait(index * 100);
      void racerEntry.car.animatePreStart();
    });

    await playLayoutAnimation(layout, 'CountdownAnimation', false);

    racers.forEach((racer) => {
      racer.car.animateRaceStart();
    });

    this.startTrackScrolling();

    const goTextAnimation = new RaceEventGoTextLocalizations();
    goTextAnimation.root.props({
      parent: this.layout.lightsContainer,
    });
    await playLayoutAnimation(goTextAnimation, 'Go_Animation');
    goTextAnimation.root.destroy({ children: true });
  }

  private startTrackScrolling(): void {
    this.layout.frameSide.forEach((side) => {
      side.tweens.create(side.tilePosition).to({ y: 62 }, 200, 'linear', true, 0, -1);
    });
  }
}
